/**
 * Warehouse — Part 5 constants.
 *
 * THE single definition of every number the warehouse spec fixes. The legacy
 * app repeated the $192 vault rate in three places; it is defined ONCE here as
 * `VAULT_RATE` and every warehouse/rebuild screen in this territory imports it.
 *
 * Nothing in this file is derived from data — these are owner-set rules.
 * Anything that IS derived from a row lives in `slotModel.ts` / `log.ts` /
 * `expectedRevenue.ts` / `palletPrediction.ts`, all of which import from here.
 */

// ── Billing ─────────────────────────────────────────────────────────────────

/** $192 per vault per month. A vault IS a pallet (owner 2026-08-20). */
export const VAULT_RATE = 192;

/** QuickBooks storage line: item 160 "Contents - Storage". */
export const QB_STORAGE_ITEM = '160';

/** The hard-coded storage billing cycle: billed-until = date entered + 3 months. */
export const BILLED_UNTIL_MONTHS = 3;

/** Average days in a month — the monthsDue divisor. */
export const DAYS_PER_MONTH = 30.4375;

// ── Physical model (§ Shared foundations) ───────────────────────────────────

/** Sections displayed left → right. 1 = Left Rack, 0 = Right Rack, 2 = Shared. */
export const SECTION_ORDER = [1, 0, 2] as const;

/** Shelf index → name. 0 = Top … 3 = Bottom. */
export const SHELF_NAMES = ['Top', 'Upper Middle', 'Lower Middle', 'Bottom'] as const;

/** The bottom shelf. Counted separately in the pallet prediction as bulk-capable. */
export const BOTTOM_SHELF_IDX = 3;

/**
 * Per-section shelf-slot totals the owner fixed:
 *   Left Rack   4 × 22 → 88   (Bay 8 is NOT blocked — owner 2026-08-14)
 *   Right Rack  18/18/18/12 → 66
 *   Shared      4 × 21 → 84   (the merged "Mattress Slots" bay is retired,
 *                              so all three of its slots are normal again)
 */
export const SECTION_SLOT_TOTALS: Record<number, number> = { 1: 88, 0: 66, 2: 84 };

/** 88 + 66 + 84. The denominator the CPO measurable "% full" divides by. */
export const TOTAL_SHELF_SLOTS = 238;

/** Staging ("not placed") slots. Excluded from every capacity denominator. */
export const STAGING_SLOTS = 40;

/**
 * Blocked slots: hatched, unclickable, no label, subtracted from capacity.
 * (Owner list — three slots, all in the Right Rack top two shelves plus one
 * Shared Space top slot.)
 */
export const BLOCKED_SLOT_IDS: readonly string[] = ['S2-R0-P2', 'S0-R0-P15', 'S0-R1-P15'];
const BLOCKED = new Set(BLOCKED_SLOT_IDS);
export function isBlockedSlot(slotId: string): boolean {
  return BLOCKED.has(slotId);
}

/** 238 − 3 blocked. The usable denominator for the §1 occupancy cards. */
export const USABLE_SHELF_SLOTS = TOTAL_SHELF_SLOTS - BLOCKED_SLOT_IDS.length;

/**
 * The merged "Mattress Slots" bay is RETIRED — every slot is a normal stacking
 * slot now. The old placeholder job number is still RECOGNIZED so legacy rows
 * render, but it is never offered for a new placement.
 */
export const RETIRED_PLACEHOLDERS: readonly string[] = ['MATTRESSES'];

/** Non-billable placeholder pallets: excluded from job roll-ups and billing. */
export const PLACEHOLDER_JOB_NUMBERS: readonly string[] = ['PDQ STORAGE', 'BULK ITEMS', 'MATTRESSES'];
const PLACEHOLDERS = new Set(PLACEHOLDER_JOB_NUMBERS);
export function isPlaceholderJobNumber(jn: string | null | undefined): boolean {
  return !!jn && PLACEHOLDERS.has(jn.trim().toUpperCase());
}

/** The bulk placeholder — bulk entries collect into its bin on a slot click. */
export const BULK_JOB_NUMBER = 'BULK ITEMS';

// ── Fill colours (§1) ───────────────────────────────────────────────────────

export const FILL_RED = '#dc2626';
export const FILL_AMBER = '#d97706';
export const FILL_GREEN = '#16a34a';

/** > 90 red · 70–90 amber · < 70 green. */
export function fillColour(pct: number): string {
  if (pct > 90) return FILL_RED;
  if (pct >= 70) return FILL_AMBER;
  return FILL_GREEN;
}

// ── Packout planner truck rows (§1.2, §6.5) ─────────────────────────────────

/** Truck row indices that mean "pack-out". */
export const PACKOUT_ROWS: readonly number[] = [3, 4, 5];
/** Truck row indices that mean "pack-back". */
export const PACKBACK_ROWS: readonly number[] = [6, 7];

export type PlannerBand = 'packout' | 'packback' | 'other';
export function plannerBand(laneIdx: number): PlannerBand {
  if (PACKOUT_ROWS.includes(laneIdx)) return 'packout';
  if (PACKBACK_ROWS.includes(laneIdx)) return 'packback';
  return 'other';
}

/** "Work at Warehouse" marker — a planner slot naming the Pompton Plains shop. */
export const POMPTON_TOKEN = '__POMPTON_PLAINS__';
export const POMPTON_MATCH = 'pompton';

/** A planner slot's duration in hours: durationSlots × 0.5, else 8 full / 4 half. */
export const SLOT_HOURS = 0.5;
export const FULL_DAY_HOURS = 8;
export const HALF_DAY_HOURS = 4;

// ── Pallet prediction (§2.1) ────────────────────────────────────────────────

/** 3 boxes per pallet. Each room is ceiling'd independently. */
export const BOXES_PER_PALLET = 3;
/** 2 bulk items per slot. Each room is ceiling'd independently. */
export const BULK_PER_SLOT = 2;
/** The prediction reads planner assignments for the next 7 days. */
export const PREDICTION_DAYS = 7;
/** demand ≥ this share of open slots → "warn". */
export const PREDICTION_WARN_RATIO = 0.7;

// ── Warehouse log (§3) ──────────────────────────────────────────────────────

export const MAX_RANGE_DAYS = 92;
export const MAX_ENTRIES_PER_POST = 200;
export const MAX_DETAIL = 400;
export const MAX_INDEX_JOBS = 600;

/** Log writes batch on a 400 ms debounce, retried, flushed on pagehide. */
export const LOG_DEBOUNCE_MS = 400;
/** The log re-reads on paint and polls every 30 s. There is no Refresh button. */
export const LOG_POLL_MS = 30_000;
/** Same action + job + person inside this window merge into one line. */
export const GROUP_WINDOW_MIN = 45;

/** The action vocabulary the log knows (§3). */
export const LOG_ACTIONS = [
  'pallets_added', 'pallets_removed', 'pallets_moved', 'pallet_edited',
  'job_checked_out', 'job_arrived',
  'schedule_added', 'schedule_removed',
  'packout_scheduled', 'packout_rescheduled', 'packout_unscheduled',
  'packback_scheduled', 'packback_rescheduled', 'packback_unscheduled',
  'packback_confirmed', 'packback_text_sent',
  'packout_zone_added', 'packout_zone_cleared',
  'packback_zone_confirmed', 'packback_zone_seeded',
  'supplies_updated', 'room_added',
] as const;
export type LogAction = (typeof LOG_ACTIONS)[number];

/**
 * ONLY these merge. Scheduling entries and confirmations never merge, however
 * close together they land.
 */
export const GROUPABLE_ACTIONS: readonly LogAction[] = [
  'pallets_added', 'pallets_removed', 'pallets_moved', 'pallet_edited', 'supplies_updated',
];
const GROUPABLE = new Set<string>(GROUPABLE_ACTIONS);
export function isGroupable(action: string): boolean {
  return GROUPABLE.has(action);
}

/** Left-border colour class per action. */
export const ACTION_CLASS: Record<string, { colour: string; icon: string; verb: string }> = {
  pallets_added:          { colour: '#16a34a', icon: '📦', verb: 'pallets added' },
  pallets_removed:        { colour: '#dc2626', icon: '📤', verb: 'pallets removed' },
  pallets_moved:          { colour: '#0891b2', icon: '↔️', verb: 'pallets moved' },
  pallet_edited:          { colour: '#6366f1', icon: '✏️', verb: 'pallet edited' },
  job_checked_out:        { colour: '#b45309', icon: '🚚', verb: 'job checked out' },
  job_arrived:            { colour: '#0d9488', icon: '🏠', verb: 'job arrived' },
  schedule_added:         { colour: '#4f46e5', icon: '🗓', verb: 'schedule added' },
  schedule_removed:       { colour: '#64748b', icon: '🗓', verb: 'schedule removed' },
  packout_scheduled:      { colour: '#4f46e5', icon: '↓', verb: 'pack-out scheduled' },
  packout_rescheduled:    { colour: '#4f46e5', icon: '↓', verb: 'pack-out rescheduled' },
  packout_unscheduled:    { colour: '#64748b', icon: '↓', verb: 'pack-out unscheduled' },
  packback_scheduled:     { colour: '#d97706', icon: '↑', verb: 'pack-back scheduled' },
  packback_rescheduled:   { colour: '#d97706', icon: '↑', verb: 'pack-back rescheduled' },
  packback_unscheduled:   { colour: '#64748b', icon: '↑', verb: 'pack-back unscheduled' },
  packback_confirmed:     { colour: '#16a34a', icon: '✓', verb: 'pack-back confirmed' },
  packback_text_sent:     { colour: '#0891b2', icon: '💬', verb: 'pack-back text sent' },
  packout_zone_added:     { colour: '#0891b2', icon: '🏷', verb: 'added to pack-out zone' },
  packout_zone_cleared:   { colour: '#64748b', icon: '🏷', verb: 'cleared from pack-out zone' },
  packback_zone_confirmed:{ colour: '#16a34a', icon: '✓', verb: 'packed back' },
  packback_zone_seeded:   { colour: '#0891b2', icon: '⇢', verb: 'moved to pack-back zone' },
  supplies_updated:       { colour: '#b45309', icon: '🧰', verb: 'supplies updated' },
  room_added:             { colour: '#6366f1', icon: '🚪', verb: 'room added' },
};

/** The chip shown against a board entry that predates the scan-only cutover. */
export const OLD_SYSTEM_CHIP = 'OLD SYSTEM';

// ── Warehouse supplies (§1.3) ───────────────────────────────────────────────

export interface SupplyColumn { key: string; label: string; items: { key: string; label: string }[] }

export const SUPPLY_COLUMNS: readonly SupplyColumn[] = [
  {
    key: 'boxes', label: 'Boxes',
    items: [
      { key: 'box_small', label: 'Small' },
      { key: 'box_medium', label: 'Medium' },
      { key: 'box_large', label: 'Large' },
    ],
  },
  {
    key: 'boxes_cont', label: 'Boxes cont.',
    items: [
      { key: 'box_wardrobe', label: 'Wardrobe' },
      { key: 'box_pm', label: 'PM' },
      { key: 'box_tv', label: 'TV' },
    ],
  },
  {
    key: 'packing', label: 'Packing Supplies',
    items: [
      { key: 'bubble_wrap', label: 'Bubble Wrap' },
      { key: 'packing_paper', label: 'Packing Paper' },
      { key: 'plastic_hand_wraps', label: 'Plastic Hand Wraps' },
      { key: 'large_plastic_rolls', label: 'Large Plastic Rolls' },
    ],
  },
  {
    key: 'palletizing', label: 'Palletizing',
    items: [
      { key: 'pallets', label: 'Pallets' },
      { key: 'mattress_bags', label: 'Mattress Bags' },
      { key: 'moving_blankets', label: 'Moving Blankets' },
      { key: 'label_paper', label: 'Label Paper' },
    ],
  },
];

// ── Rebuild Summary (§6) ────────────────────────────────────────────────────

/** Estimate stage colours, in order. */
export const STAGE_COLOURS = ['#4f46e5', '#0891b2', '#0d9488', '#d97706', '#16a34a'] as const;
/** Extra muted stages appended to every stage column. */
export const STAGE_MUTED_COLOUR = '#94a3b8';
export const STAGE_NOT_SET = 'Not Set';
export const STAGE_NOT_MOVING = 'Not Moving Forward';

/** Work-in-progress day bands: > 30 red, > 14 amber, else green. */
export function wipDayColour(days: number | null): string {
  if (days == null) return STAGE_MUTED_COLOUR;
  if (days > 30) return FILL_RED;
  if (days > 14) return FILL_AMBER;
  return FILL_GREEN;
}

/** Jimmy runs Rebuild and the Warehouse; matched across three participant roles. */
export const JIMMY = 'Jimmy Bates';
export const JIMMY_SPELLINGS: readonly string[] = ['jimmy bates', 'jim bates', 'james bates', 'jimmy'];

/** Expected storage revenue proration, by measurable column. */
export const STORAGE_PRORATION: Record<'weekly' | 'monthly' | 'quarterly' | 'yearly', number> = {
  weekly: 12 / 52,
  monthly: 1,
  quarterly: 3,
  yearly: 12,
};

/** Yodeck read-only TV views: ?wh-tv=left|right|shared (plus the whole-warehouse view). */
export const TV_PARAM = 'wh-tv';
export const TV_SECTIONS: Record<string, number> = { left: 1, right: 0, shared: 2 };
